import type { GetListParams, Identifier } from "ra-core";
import {
  buildCompanyIdInFilter,
  takeAccountsWebsiteProjectFilter,
} from "@/modules/accounts/accountsListFilterUtils";

export type WebsiteProjectCompanyListResult =
  | { empty: true }
  | { empty: false; params: GetListParams };

/**
 * Resolves the Accounts UI-only `has_website_project@eq` facet into `id@in`
 * using company ids from website deals. Callers supply the deal lookup.
 */
export const applyWebsiteProjectCompanyListFilter = async (
  params: GetListParams,
  loadWebsiteCompanyIds: () => Promise<Identifier[]>,
): Promise<WebsiteProjectCompanyListResult> => {
  const { wantsWebsiteProject, rest } = takeAccountsWebsiteProjectFilter(
    params.filter as Record<string, unknown> | undefined,
  );
  if (!wantsWebsiteProject) {
    return { empty: false, params: { ...params, filter: rest } };
  }

  const ids = await loadWebsiteCompanyIds();
  if (ids.length === 0) {
    return { empty: true };
  }

  return {
    empty: false,
    params: {
      ...params,
      filter: {
        ...rest,
        "id@in": buildCompanyIdInFilter(ids),
      },
    },
  };
};

const WEBSITE_DEALS_PAGE_SIZE = 1000;

/** Collect distinct company_id from deals with project_type = website. */
export const loadWebsiteDealCompanyIds = async (
  getDealList: (params: GetListParams) => Promise<{
    data: Array<{ company_id?: Identifier | null }>;
  }>,
): Promise<Identifier[]> => {
  const ids = new Set<string>();
  let page = 1;

  for (;;) {
    const { data } = await getDealList({
      pagination: { page, perPage: WEBSITE_DEALS_PAGE_SIZE },
      sort: { field: "id", order: "ASC" },
      filter: { "project_type@eq": "website" },
    });

    for (const deal of data) {
      if (deal.company_id != null && deal.company_id !== "") {
        ids.add(String(deal.company_id));
      }
    }

    if (data.length < WEBSITE_DEALS_PAGE_SIZE) break;
    page += 1;
  }

  return Array.from(ids);
};
